'use client';

import { c as _c } from "react/compiler-runtime";
import { Checkbox, Stack, Tooltip, Typography } from '@mui/material';
import type { ClassificationCategory, Condition, DataClassificationEntityOption } from '@repo/api-gw-sdk';
import { useMemo } from 'react';
import { Accordion, AccordionDetails, AccordionSummary } from '@/components/accordion';
import { Loader } from '@/components/layout/loading';
import { useDAL } from '@/contexts/useDAL';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';
import { PiiStrings } from '@/data/inventory/data';
import { useClassificationCategories, useDataClassFilterOptions } from '@/data/inventory/useClassificationCategories';
import { createValueCondition, isValueCondition } from '@/types/advanceFilter';
import { MultiSelectFilter, type FilterProps } from './filters';
const DATA_CLASSES = 'dataClasses';
const DATA_CLASS_ENTITIES = 'dataClassEntities';
const OTHER_CATEGORY = 'Other';
interface DataClassGroup {
  id: string;
  description?: string;
  children: string[];
}
const buildGroups = (categories: ClassificationCategory[], entities: DataClassificationEntityOption[]): DataClassGroup[] => {
  const groups = new Map<string, DataClassGroup>(categories.map(c => [c.name, {
    id: c.name,
    description: c.description,
    children: []
  }]));
  for (const entity of entities) {
    const cats = entity.categories.length ? entity.categories : [OTHER_CATEGORY];
    for (const cat of cats) {
      const group = groups.get(cat) ?? {
        id: cat,
        children: []
      };
      group.children.push(entity.name);
      groups.set(cat, group);
    }
  }
  return [...groups.values()].map(group => ({
    ...group,
    children: [...new Set(group.children)].sort()
  }));
};
const findValues = (conditions: Condition[], property: string): string[] => {
  const found = conditions.find(c => isValueCondition(c) && c.property === property);
  return found?.value ?? [];
};
const isDataClassCondition = (condition: Condition) => isValueCondition(condition) && (condition.property === DATA_CLASSES || condition.property === DATA_CLASS_ENTITIES);
const buildDataClassConditions = (classes: string[], entities: string[]): Condition[] => {
  const out: Condition[] = [];
  if (classes.length) {
    out.push(createValueCondition(DATA_CLASSES, classes));
  }
  if (entities.length) {
    out.push(createValueCondition(DATA_CLASS_ENTITIES, entities));
  }
  return out;
};
const stripDataClassConditions = (conditions: Condition[]): Condition[] => conditions.filter(c => !isDataClassCondition(c));
const ROW_GAP = '12px';
const CHECKBOX_PADDING = '3px';
const CHECKBOX_SLOT_WIDTH = '26px';
const ARROW_SLOT_WIDTH = '26px';
// marginLeft for child rows so the child checkbox sits under the parent arrow.
const CHILD_INDENT = `calc(${CHECKBOX_SLOT_WIDTH} + ${ROW_GAP})`;
export const entityLabel = (entity: string) => (PiiStrings[entity] ?? entity).toLowerCase();
const DataClassFilterGroup = t0 => {
  const $ = _c(37);
  const {
    group,
    parentSelected,
    selectedEntities,
    onParentChange,
    onChildChange
  } = t0;
  let t1;
  if ($[0] !== group.children || $[1] !== selectedEntities) {
    let t2;
    if ($[3] !== selectedEntities) {
      t2 = c => selectedEntities.includes(c);
      $[3] = selectedEntities;
      $[4] = t2;
    } else {
      t2 = $[4];
    }
    t1 = group.children.some(t2);
    $[0] = group.children;
    $[1] = selectedEntities;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const childSelected = t1;
  const hasChildren = group.children.length > 0;
  let t2;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      flexDirection: "row",
      "& .MuiAccordionSummary-content": {
        margin: 0,
        paddingLeft: 0
      },
      "& .accordion-arrow": {
        transition: "transform 0.1s"
      },
      "&.Mui-expanded .accordion-arrow": {
        transform: "rotate(90deg)"
      }
    };
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  let t3;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      alignItems: "center",
      gap: ROW_GAP,
      width: "100%"
    };
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      width: CHECKBOX_SLOT_WIDTH,
      alignItems: "center",
      justifyContent: "center"
    };
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = `filter-dataClasses-${group.id}`;
  let t6;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      padding: CHECKBOX_PADDING
    };
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const t7 = !parentSelected && childSelected;
  let t8;
  if ($[9] !== onParentChange || $[10] !== parentSelected || $[11] !== t5 || $[12] !== t7) {
    t8 = <Stack sx={t4}><Checkbox data-testid={t5} sx={t6} size="small" checked={parentSelected} indeterminate={t7} onClick={_temp} onChange={onParentChange} /></Stack>;
    $[9] = onParentChange;
    $[10] = parentSelected;
    $[11] = t5;
    $[12] = t7;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      width: ARROW_SLOT_WIDTH,
      alignItems: "center",
      justifyContent: "center"
    };
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== hasChildren) {
    t10 = hasChildren && <i className="material-symbols-arrow-right-rounded accordion-arrow" />;
    $[15] = hasChildren;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10) {
    t11 = <Stack sx={t9}>{t10}</Stack>;
    $[17] = t10;
    $[18] = t11;
  } else {
    t11 = $[18];
  }
  const t12 = group.description ?? group.id;
  let t13;
  if ($[19] !== group.id) {
    t13 = <Typography variant="subtitle2">{group.id}</Typography>;
    $[19] = group.id;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== t12 || $[22] !== t13) {
    t14 = <Tooltip title={t12}>{t13}</Tooltip>;
    $[21] = t12;
    $[22] = t13;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  let t15;
  if ($[24] !== t11 || $[25] !== t14 || $[26] !== t8) {
    t15 = <AccordionSummary expandIcon={null} sx={t2}><Stack direction="row" sx={t3}>{t8}{t11}{t14}</Stack></AccordionSummary>;
    $[24] = t11;
    $[25] = t14;
    $[26] = t8;
    $[27] = t15;
  } else {
    t15 = $[27];
  }
  let t16;
  if ($[28] !== group.children || $[29] !== hasChildren || $[30] !== onChildChange || $[31] !== parentSelected || $[32] !== selectedEntities) {
    t16 = hasChildren && <AccordionDetails sx={{
      px: "0px",
      py: "4px"
    }}><Stack sx={{
        gap: "0px"
      }}>{group.children.map(entity => <Stack key={entity} direction="row" sx={{
          alignItems: "center",
          gap: ROW_GAP,
          marginLeft: CHILD_INDENT
        }}><Stack sx={{
            width: CHECKBOX_SLOT_WIDTH,
            alignItems: "center",
            justifyContent: "center"
          }}><Checkbox data-testid={`filter-dataClassEntities-${entity}`} sx={{
              padding: CHECKBOX_PADDING
            }} size="small" checked={parentSelected || selectedEntities.includes(entity)} onChange={() => onChildChange(entity)} /></Stack><Typography variant="body2">{entityLabel(entity)}</Typography></Stack>)}</Stack></AccordionDetails>;
    $[28] = group.children;
    $[29] = hasChildren;
    $[30] = onChildChange;
    $[31] = parentSelected;
    $[32] = selectedEntities;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== t15 || $[35] !== t16) {
    t17 = <Accordion>{t15}{t16}</Accordion>;
    $[34] = t15;
    $[35] = t16;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  return t17;
};
const DataClassesWithEntitiesFilter = ({
  conditions,
  onChange
}: FilterProps) => {
  const dal = useDAL();
  const {
    categories,
    isLoading: categoriesLoading
  } = useClassificationCategories();
  const {
    data: body,
    isLoading: entitiesLoading
  } = dal.inventory.entities.useList();
  const groups = useMemo(() => buildGroups(categories, body?.entities ?? []), [categories, body?.entities]);
  if (categoriesLoading || entitiesLoading) {
    return <Loader />;
  }
  const selectedClasses = findValues(conditions, DATA_CLASSES);
  const selectedEntities = findValues(conditions, DATA_CLASS_ENTITIES);
  const apply = (classes: string[], entities: string[]) => {
    onChange([...stripDataClassConditions(conditions), ...buildDataClassConditions(classes, entities)]);
  };
  const onParentChange = (group: DataClassGroup) => {
    const isOn = selectedClasses.includes(group.id);
    const nextClasses = isOn ? selectedClasses.filter(c => c !== group.id) : [...selectedClasses, group.id];
    const nextEntities = selectedEntities.filter(e => !group.children.includes(e));
    apply(nextClasses, nextEntities);
  };
  const onChildChange = (group_0: DataClassGroup, entity: string) => {
    const nextClasses_0 = selectedClasses.filter(c_0 => c_0 !== group_0.id);
    const otherEntities = selectedEntities.filter(e_0 => !group_0.children.includes(e_0));
    // Selecting a child while the parent class is selected swaps the broad
    // class for that single entity in the same group.
    if (selectedClasses.includes(group_0.id)) {
      apply(nextClasses_0, [...otherEntities, entity]);
      return;
    }
    const nextEntities_0 = selectedEntities.includes(entity) ? selectedEntities.filter(e_1 => e_1 !== entity) : [...selectedEntities, entity];
    apply(nextClasses_0, nextEntities_0);
  };
  return <Stack sx={{
    gap: '4px'
  }}>
      {groups.map(group_1 => <DataClassFilterGroup key={group_1.id} group={group_1} parentSelected={selectedClasses.includes(group_1.id)} selectedEntities={selectedEntities} onParentChange={() => onParentChange(group_1)} onChildChange={entity_0 => onChildChange(group_1, entity_0)} />)}
    </Stack>;
};
const FlatDataClassesFilter = props => {
  const $ = _c(3);
  const {
    options
  } = useDataClassFilterOptions();
  let t0;
  if ($[0] !== options || $[1] !== props) {
    t0 = <MultiSelectFilter {...props} options={options} />;
    $[0] = options;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
export const DataClassesFilter = props => {
  const $ = _c(3);
  const {
    EnableInventoryEntitiesFilter
  } = useFeatureFlags();
  let t0;
  if ($[0] !== EnableInventoryEntitiesFilter || $[1] !== props) {
    t0 = EnableInventoryEntitiesFilter === true ? <DataClassesWithEntitiesFilter {...props} /> : <FlatDataClassesFilter {...props} />;
    $[0] = EnableInventoryEntitiesFilter;
    $[1] = props;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  return t0;
};
function _temp(event) {
  return event.stopPropagation();
}